import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Divider,
  IconButton,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import {
  createEventAttribute,
  updateEventAttribute,
  deleteInventoryItem,
} from "../../../../../data-store/actions/events-actions";
import { showSnackbar } from "../../../../../data-store/actions/organizers-actions";
import {
  listarFuentesDisponibles,
  listarFilasDeMapeo,
  deduplicarFuentes,
  productosDeFuentes,
  VALOR_PRODUCTO_COMPLETO,
} from "./inventoryLogic";

const SIN_ASIGNAR = -1;

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: "20px",
    textAlign: "left",
    minHeight: "300px",
    overflowY: "scroll",
    backgroundColor: "#e0e0e0",
  },
  seccion: {
    marginTop: 20,
    marginBottom: 10,
  },
  filaMapeo: {
    padding: "6px 0",
    borderBottom: "1px solid #ddd",
  },
  filaOpcion: {
    padding: "6px 0",
  },
}));

function initOpciones(existingItem) {
  if (!existingItem || !existingItem.opciones) return [];
  return Object.values(existingItem.opciones).map((o) => ({
    nombre: o.nombre,
    capacidad: o.capacidad,
    vendidos: o.vendidos || 0,
    agotado: o.agotado === true,
  }));
}

function initAsignacionPorValor(existingItem) {
  const resultado = {};
  if (!existingItem || !existingItem.opciones) return resultado;
  Object.values(existingItem.opciones).forEach((o, i) => {
    Object.keys(o.valoresOrigen || {}).forEach((v) => {
      resultado[v] = i;
    });
  });
  return resultado;
}

function initFuentesKeys(existingItem) {
  if (!existingItem || !existingItem.fuentes) return [];
  return existingItem.fuentes.map((f) => {
    if (f.origen === "campo") return "campo|" + f.iDCampo;
    if (f.origen === "producto") return "producto|" + f.codigoProducto;
    return "productoCampo|" + f.codigoProducto + "|" + f.iDCampo;
  });
}

function limpiarFuenteParaGuardar(f) {
  const obj = { origen: f.origen };
  if (f.iDCampo !== undefined) obj.iDCampo = f.iDCampo;
  if (f.codigoProducto !== undefined) obj.codigoProducto = f.codigoProducto;
  return obj;
}

export default function InventoryItemForm({ itemKey, existingItem, onGuardado }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const eventDetail = useSelector((state) => state.events.eventDetail);
  const registryConfiguration = useSelector(
    (state) => state.registry.registryConfiguration
  );

  const [nombre, setNombre] = useState(existingItem ? existingItem.nombre : "");
  const [fuentesKeys, setFuentesKeys] = useState(() => initFuentesKeys(existingItem));
  const [mapeoState, setMapeoState] = useState(() => ({
    opciones: initOpciones(existingItem),
    asignacionPorValor: initAsignacionPorValor(existingItem),
  }));

  const fuentesDisponibles = useMemo(
    () => listarFuentesDisponibles(registryConfiguration),
    [registryConfiguration]
  );

  const fuentesElegidas = useMemo(
    () => fuentesDisponibles.filter((f) => fuentesKeys.indexOf(f.key) >= 0),
    [fuentesDisponibles, fuentesKeys]
  );

  const filasMapeo = useMemo(
    () => listarFilasDeMapeo(fuentesElegidas),
    [fuentesElegidas]
  );

  // Auto-genera una opción por cada valor crudo que todavía no tenga
  // asignación (p.ej. la primera vez que se elige una fuente). Los valores
  // ya asignados (al editar un item existente, o tras un ajuste manual) no
  // se tocan.
  useEffect(() => {
    setMapeoState((prev) => {
      const asignacionPorValor = { ...prev.asignacionPorValor };
      const opciones = [...prev.opciones];
      let huboCambios = false;
      filasMapeo.forEach((fila) => {
        if (asignacionPorValor[fila.rawValor] === undefined) {
          asignacionPorValor[fila.rawValor] = opciones.length;
          opciones.push({ nombre: fila.etiquetaValor, capacidad: 0 });
          huboCambios = true;
        }
      });
      return huboCambios ? { opciones, asignacionPorValor } : prev;
    });
  }, [filasMapeo]);

  function toggleFuente(key) {
    setFuentesKeys((prev) =>
      prev.indexOf(key) >= 0 ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }

  function cambiarAsignacion(rawValor, nuevoIndex) {
    setMapeoState((prev) => ({
      ...prev,
      asignacionPorValor: { ...prev.asignacionPorValor, [rawValor]: nuevoIndex },
    }));
  }

  function agregarOpcion() {
    setMapeoState((prev) => ({
      ...prev,
      opciones: [...prev.opciones, { nombre: "Nueva opción", capacidad: 0 }],
    }));
  }

  function cambiarNombreOpcion(index, nuevoNombre) {
    setMapeoState((prev) => ({
      ...prev,
      opciones: prev.opciones.map((o, i) =>
        i === index ? { ...o, nombre: nuevoNombre } : o
      ),
    }));
  }

  function cambiarCapacidadOpcion(index, nuevaCapacidad) {
    setMapeoState((prev) => ({
      ...prev,
      opciones: prev.opciones.map((o, i) =>
        i === index ? { ...o, capacidad: nuevaCapacidad } : o
      ),
    }));
  }

  function eliminarOpcion(index) {
    setMapeoState((prev) => {
      const opciones = prev.opciones.filter((_, i) => i !== index);
      const asignacionPorValor = {};
      for (var k in prev.asignacionPorValor) {
        const v = prev.asignacionPorValor[k];
        if (v === index) asignacionPorValor[k] = SIN_ASIGNAR;
        else if (v > index) asignacionPorValor[k] = v - 1;
        else asignacionPorValor[k] = v;
      }
      return { opciones, asignacionPorValor };
    });
  }

  function guardar() {
    const valoresOrigenPorOpcion = mapeoState.opciones.map(() => []);
    for (var rawValor in mapeoState.asignacionPorValor) {
      const idx = mapeoState.asignacionPorValor[rawValor];
      if (idx >= 0 && valoresOrigenPorOpcion[idx]) {
        valoresOrigenPorOpcion[idx].push(rawValor);
      }
    }

    const fuentesFinales = deduplicarFuentes(fuentesElegidas);
    const productosDelItem = productosDeFuentes(fuentesFinales, registryConfiguration);
    const eventoIndex = "evento_" + eventDetail.iDEvento;

    const opcionesParaGuardar = {};
    const escriturasProducto = [];
    mapeoState.opciones.forEach((op, i) => {
      const capacidad = Number(op.capacidad) || 0;
      const vendidos = op.vendidos || 0;
      // Se evalúa aquí mismo (no solo se espera al backend) para que el
      // panel refleje "agotado" de inmediato al guardar una capacidad menor
      // o igual a lo ya vendido/seleccionado. El backend (Cloud Function)
      // hace este mismo cálculo al reaccionar al cambio, pero no hay que
      // esperarlo para que quede reflejado.
      const agotado = capacidad > 0 && vendidos >= capacidad;
      opcionesParaGuardar["opcion_" + (i + 1)] = {
        nombre: op.nombre,
        capacidad,
        vendidos,
        agotado,
        valoresOrigen: valoresOrigenPorOpcion[i].reduce((acc, v) => {
          acc[v] = true;
          return acc;
        }, {}),
      };

      // Si esta opción representa un producto completo (sin desglose), su
      // estado agotado se refleja también en agotadoInventario del producto
      // — es lo que usa CardProduct para deshabilitar la compra.
      if (valoresOrigenPorOpcion[i].indexOf(VALOR_PRODUCTO_COMPLETO) >= 0) {
        productosDelItem.forEach((productoKey) => {
          escriturasProducto.push(
            updateEventAttribute(
              eventoIndex,
              "inscripcionesConfiguracion/productosAdicionales/" + productoKey + "/",
              { agotadoInventario: agotado }
            )
          );
        });
      }
    });

    const nuevoItem = {
      nombre: nombre || "Item sin nombre",
      fuentes: fuentesFinales.map(limpiarFuenteParaGuardar),
      opciones: opcionesParaGuardar,
    };

    escriturasProducto.forEach((accion) => dispatch(accion));

    const key = itemKey || "item_" + Date.now();
    dispatch(
      createEventAttribute(
        eventoIndex,
        "inscripcionesConfiguracion/inventarios/" + key + "/",
        nuevoItem
      )
    ).then(() => {
      dispatch(
        showSnackbar(
          true,
          itemKey
            ? "Item de inventario actualizado exitosamente"
            : "Item de inventario creado exitosamente",
          5000
        )
      );
    });
    if (onGuardado) onGuardado();
  }

  function borrar() {
    if (!itemKey) return;
    dispatch(deleteInventoryItem("evento_" + eventDetail.iDEvento, itemKey));
    if (onGuardado) onGuardado();
  }

  return (
    <Paper className={classes.paper}>
      <Typography gutterBottom variant={"h4"} component="h2">
        {existingItem ? "Editar item de inventario" : "Crear item de inventario"}
      </Typography>

      <TextField
        fullWidth
        label="Nombre del item (ej. Tallas de playera)"
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        style={{ marginBottom: 20 }}
      />

      <Typography variant="h6" className={classes.seccion}>
        Fuentes a controlar
      </Typography>
      <Typography variant="body2" gutterBottom>
        Selecciona uno o más campos/productos. Si varios se refieren a lo
        mismo (ej. dos selects de talla distintos), puedes elegirlos todos
        aquí y mapear sus valores a las mismas opciones abajo.
      </Typography>
      {fuentesDisponibles.map((f) => (
        <FormControlLabel
          key={f.key}
          style={{ display: "block" }}
          control={
            <Checkbox
              checked={fuentesKeys.indexOf(f.key) >= 0}
              onChange={() => toggleFuente(f.key)}
            />
          }
          label={f.etiqueta}
        />
      ))}

      {filasMapeo.length > 0 && (
        <>
          <Divider style={{ margin: "20px 0" }} />
          <Typography variant="h6" className={classes.seccion}>
            Mapeo de valores a opciones
          </Typography>
          <Typography variant="body2" gutterBottom>
            Indica a qué opción del inventario corresponde cada valor crudo
            de las fuentes elegidas. Cambia la opción de una fila para
            fusionarla con otra (ej. "S" y "Chica" apuntando a la misma).
          </Typography>
          {filasMapeo.map((fila, i) => (
            <Grid
              container
              spacing={1}
              alignItems="center"
              key={i}
              className={classes.filaMapeo}
            >
              <Grid item xs={7}>
                <Typography variant="body2">
                  {fila.etiquetaFuente} — <b>{fila.etiquetaValor}</b>
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Select
                  fullWidth
                  value={
                    mapeoState.asignacionPorValor[fila.rawValor] !== undefined
                      ? mapeoState.asignacionPorValor[fila.rawValor]
                      : SIN_ASIGNAR
                  }
                  onChange={(e) =>
                    cambiarAsignacion(fila.rawValor, e.target.value)
                  }
                >
                  <MenuItem value={SIN_ASIGNAR}>Sin asignar</MenuItem>
                  {mapeoState.opciones.map((op, idx) => (
                    <MenuItem key={idx} value={idx}>
                      {op.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
          ))}

          <Divider style={{ margin: "20px 0" }} />
          <Typography variant="h6" className={classes.seccion}>
            Opciones y capacidad
          </Typography>
          {mapeoState.opciones.map((op, i) => (
            <Grid
              container
              spacing={1}
              alignItems="center"
              key={i}
              className={classes.filaOpcion}
            >
              <Grid item xs={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nombre de la opción"
                  value={op.nombre}
                  onChange={(e) => cambiarNombreOpcion(i, e.target.value)}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Capacidad"
                  value={op.capacidad}
                  onChange={(e) => cambiarCapacidadOpcion(i, e.target.value)}
                />
              </Grid>
              <Grid item xs={3}>
                {existingItem && (
                  <Typography variant="body2">
                    Vendidos: {op.vendidos || 0} — Disponible:{" "}
                    {(Number(op.capacidad) || 0) - (op.vendidos || 0)}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={1}>
                <IconButton onClick={() => eliminarOpcion(i)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button size="small" onClick={agregarOpcion} style={{ marginTop: 10 }}>
            Agregar opción manualmente
          </Button>
        </>
      )}

      <Divider style={{ margin: "20px 0" }} />
      <Button variant="contained" color="primary" onClick={guardar}>
        Guardar item de inventario
      </Button>
      {existingItem && (
        <Button
          variant="outlined"
          color="secondary"
          style={{ marginLeft: 10 }}
          onClick={borrar}
        >
          Eliminar item
        </Button>
      )}
    </Paper>
  );
}
